import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
import { mapConductorToPromoBanners, mapImpToPromoBanners, mapPromoBanners } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/__internal__/getBannerMappings";
import { BANNERS_SECTION_WITH_ORDERED_BANNERS_MAP, BannerSectionVariant } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/__internal__/siteBannersConsts";
import { useFOIBannerOrder } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/__internal__/useFOIBannerOrder";
import { PromoBannerType } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/bannerOrder";
import { useConductorComponent } from "@faire/retailer-visitor-shared/features/Conductor/useConductorComponent";
import { usePromoBanners } from "@faire/retailer-visitor-shared/stores/ui/usePromoBanners";
import { ConductorSurface } from "@faire/web-api--source/indigofair/data/ConductorSurface";
import { FeatureName } from "@faire/web-api--source/indigofair/data/FeatureName";
import { useMemo } from "react";
/**
 * This hook is used to return the correct order of banners to be displayed, prioritizing the backend banners.
 * 1. Get eligible banners from backend (conductor and IMP).
 * 2. Get eligible banners from frontend.
 * 3. Narrow down rotation section of banners while keeping the content of the banners.
 * 4. Map resulting list of banners to FE banner to be rendered by MergedBanners.
 */ export var useSiteBannersList = function() {
    // conductor: only shows FOI banners for eligible retailers right now -> eventually will get all banners from here
    var _useConductorComponent = useConductorComponent({
        surface: ConductorSurface.SITE_WIDE_TOP_BANNER
    }), featureName = _useConductorComponent.featureName, conductorSduiViewLayout = _useConductorComponent.conductorSduiViewLayout;
    // deprecated IMP banners and FE banners -> to be migrated to conductor
    var _usePromoBanners = usePromoBanners(), inProductMessagingBanners = _usePromoBanners.inProductMessagingBanners, banners = _usePromoBanners.banners;
    var conductorBannerTypes = mapConductorToPromoBanners(featureName !== null && featureName !== void 0 ? featureName : FeatureName.FEATURE_NAME_UNKNOWN, conductorSduiViewLayout);
    var impBannerTypes = mapImpToPromoBanners(inProductMessagingBanners);
    var promoBannerTypes = mapPromoBanners(banners);
    var foiBannerOrder = useFOIBannerOrder();
    // Inverse order, last insertion will override previous ones
    var mergedBanners = useMemo(function() {
        return new Map(_to_consumable_array(promoBannerTypes).concat(_to_consumable_array(impBannerTypes), _to_consumable_array(conductorBannerTypes)));
    }, [
        promoBannerTypes,
        impBannerTypes,
        conductorBannerTypes
    ]);
    return useMemo(function() {
        return getRotationSection(mergedBanners, foiBannerOrder);
    }, [
        mergedBanners,
        foiBannerOrder
    ]);
};
var getRotationSection = function(promoBanners, foiBannerOrder) {
    var _obj;
    // initialize mapping for each section with empty arrays
    var bannerSectionToPromoMapping = (_obj = {}, _define_property(_obj, BannerSectionVariant.FAIRE_DIRECT_INCENTIVES, []), _define_property(_obj, BannerSectionVariant.MARKET_COUNTDOWN, []), _define_property(_obj, BannerSectionVariant.FIRST_ORDER_INCENTIVES, []), _define_property(_obj, BannerSectionVariant.POST_FCO_BANNERS, []), _define_property(_obj, BannerSectionVariant.ADDITIONAL_BANNERS, []), _define_property(_obj, BannerSectionVariant.EMPLOYEE_GRANTED_FREE_FREIGHT, []), _obj);
    var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
    try {
        // Map banners to sections
        for(var _iterator = promoBanners[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
            var _step_value = _sliced_to_array(_step.value, 2), bannerType = _step_value[0], bannerContent = _step_value[1];
            var _iteratorNormalCompletion1 = true, _didIteratorError1 = false, _iteratorError1 = undefined;
            try {
                for(var _iterator1 = Object.entries(BANNERS_SECTION_WITH_ORDERED_BANNERS_MAP)[Symbol.iterator](), _step1; !(_iteratorNormalCompletion1 = (_step1 = _iterator1.next()).done); _iteratorNormalCompletion1 = true){
                    var _step_value1 = _sliced_to_array(_step1.value, 2), section = _step_value1[0], sectionBannerTypes = _step_value1[1];
                    if (sectionBannerTypes.includes(bannerType)) {
                        bannerSectionToPromoMapping[section].push(_define_property({}, bannerType, bannerContent));
                    }
                }
            } catch (err) {
                _didIteratorError1 = true;
                _iteratorError1 = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion1 && _iterator1.return != null) {
                        _iterator1.return();
                    }
                } finally{
                    if (_didIteratorError1) {
                        throw _iteratorError1;
                    }
                }
            }
        }
    } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
    } finally{
        try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
                _iterator.return();
            }
        } finally{
            if (_didIteratorError) {
                throw _iteratorError;
            }
        }
    }
    var _iteratorNormalCompletion2 = true, _didIteratorError2 = false, _iteratorError2 = undefined;
    try {
        var _loop = function() {
            var _step_value = _sliced_to_array(_step2.value, 2), section = _step_value[0], eligibleSectionBannerTypes = _step_value[1];
            if (eligibleSectionBannerTypes.length === 0) {
                return "continue";
            }
            var shouldSkipPostFcoSection = parseInt(section) === BannerSectionVariant.POST_FCO_BANNERS && !eligibleSectionBannerTypes.some(function(banner) {
                var bannerType = Object.keys(banner)[0];
                return bannerType === PromoBannerType.POST_FCO_INCENTIVE_DISCOUNT_JUNE_2024;
            });
            if (shouldSkipPostFcoSection) {
                return "continue";
            }
            // sort banners in section by priority
            var sectionBannersOrder = BANNERS_SECTION_WITH_ORDERED_BANNERS_MAP[section];
            var sortOrder = parseInt(section) === BannerSectionVariant.FIRST_ORDER_INCENTIVES ? foiBannerOrder : sectionBannersOrder;
            var eligibleBannersShown = eligibleSectionBannerTypes.filter(function(banner) {
                var bannerType = Object.keys(banner)[0];
                return sortOrder.includes(bannerType);
            });
            var sortedBanners = eligibleBannersShown.sort(function(a, b) {
                var bannerA = Object.keys(a)[0];
                var bannerB = Object.keys(b)[0];
                return sortOrder.indexOf(bannerA) - sortOrder.indexOf(bannerB);
            });
            return {
                v: sortedBanners
            };
        };
        // Return the first non-empty section
        for(var _iterator2 = Object.entries(bannerSectionToPromoMapping)[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true){
            var _ret = _loop();
            if (_type_of(_ret) === "object") return _ret.v;
        }
    } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
    } finally{
        try {
            if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
                _iterator2.return();
            }
        } finally{
            if (_didIteratorError2) {
                throw _iteratorError2;
            }
        }
    }
    return [];
};
